import {makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {IRosterPlayer, IRosterItem} from "data/types/roster";
import type {
	IContestStat,
	IContestPlayerStatsStore,
} from "data/stores/contest_player_stats/contest_player_stats.store";
import {Bindings} from "data/constants/bindings";
import type {IUserRosterStore} from "data/stores/user_roster/user_roster.store";
import {IGameBar} from "data/stores/game_bar/game_bar.store";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import {RequestState, ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage} from "data/utils";
import type {ILocalizationStore} from "data/stores/localization/localization.store";

interface IParams {
	userId: number;
	contestId: number;
}
interface IRosterPlayerWithFantasyPoints extends IRosterPlayer {
	fantasyPoints: IContestStat["fantasyPoints"] | null;
}

export interface ILeaderboardRosterViewController extends ViewController<IParams> {
	i18n: ILocalizationStore;
	get roster(): IRosterItem | undefined;
	get gameBar(): IGameBar | undefined;
	get players(): IRosterPlayerWithFantasyPoints[];
	get teamName(): string;
	get isLoading(): boolean;
}

@injectable()
export class LeaderboardRosterViewController implements ILeaderboardRosterViewController {
	@observable _userId: number = 0;
	@observable _contestId: number = 0;
	@observable _teamName: string = "-";
	@observable _requestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserRosterStore) private readonly _userRosterStore: IUserRosterStore,
		@inject(Bindings.RankingsStore) private readonly _rankingsStore: IRankingsStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.ContestPlayerStatsStore)
		private readonly _contestPlayerStatsStore: IContestPlayerStatsStore
	) {
		makeAutoObservable(this);
	}

	get roster() {
		return this._userRosterStore.userRoster;
	}

	get gameBar() {
		return this._userRosterStore.userGameBar;
	}

	get players(): IRosterPlayerWithFantasyPoints[] {
		const players = this._userRosterStore.userRoster?.players ?? [];

		return players.map((player) => ({
			...player,
			fantasyPoints:
				this._contestPlayerStatsStore.getPlayerStats(player.playerId)?.fantasyPoints ??
				null,
		}));
	}

	get teamName(): string {
		return this._userRosterStore.displayName || "--";
	}

	get isLoading(): boolean {
		return [RequestState.IDLE, RequestState.PENDING].includes(this._requestState);
	}

	init(param: IParams) {
		this._contestId = param.contestId;
		this._userId = param.userId;

		this.showRoster();
	}

	onChange(param: IParams) {
		this.init(param);
	}

	setTeamName = () => {
		const rankings = this._rankingsStore.rankings;
		const teamName = rankings.find((it) => it.userId === this._userId);
		this._teamName = teamName?.displayName || "--";
	};

	showRoster = () => {
		this._requestState = RequestState.PENDING;
		if (this._userId) {
			void this._userRosterStore
				.fetchUserRoster(this._contestId, this._userId)
				.then(() => {
					runInAction(() => {
						this._requestState = RequestState.SUCCESS;
					});
				})
				.catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});

					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});
				});
		}
	};
}
